import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useInView } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  Award, Heart, Users, Music, Globe, Star, BookOpen, 
  Calendar, MapPin, Play, X, ChevronRight, Drum, 
  GraduationCap, Sparkles, Trophy, Crown, Flame, Zap, Film, Clapperboard, Download, Video, FileText, Lock
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// PDF Download helper function
import { jsPDF } from 'jspdf';

// Action Plan PDF - 1 page, Arial 11pt, 1.5 line spacing
const downloadActionPlanPDF = () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - (margin * 2);
  const lineHeight = 7; // 1.5 spacing for 11pt
  
  let y = 20;
  
  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('THE ANCESTRAL STRING INSTRUMENTS OF THE ORCHESTRA', pageWidth / 2, y, { align: 'center' });
  y += 6;
  doc.setFontSize(12);
  doc.text('Action Plan', pageWidth / 2, y, { align: 'center' });
  y += 12;
  
  // Set body font - Arial equivalent is Helvetica in jsPDF
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  
  // Project Overview
  doc.setFont('helvetica', 'bold');
  doc.text('PROJECT OVERVIEW', margin, y);
  y += lineHeight;
  doc.setFont('helvetica', 'normal');
  const overview = 'This project documents and preserves the African ancestral origins of Western orchestral string instruments through video production, educational content, and live performances featuring Mestre Curtis Pierre and traditional master craftsmen.';
  const overviewLines = doc.splitTextToSize(overview, maxWidth);
  doc.text(overviewLines, margin, y);
  y += overviewLines.length * lineHeight + 5;
  
  // Timeline
  doc.setFont('helvetica', 'bold');
  doc.text('TIMELINE (12 Months)', margin, y);
  y += lineHeight;
  doc.setFont('helvetica', 'normal');
  
  const timeline = [
    'Months 1-2: Pre-production planning, instrument sourcing, craftsmen coordination',
    'Months 3-5: Video documentation of 5 ancestral instruments (construction process)',
    'Months 6-8: Educational content development, platform integration',
    'Months 9-10: Live performance rehearsals with orchestra and Escola de Samba Casa Samba ensemble',
    'Months 11-12: Public performances, content release, impact documentation'
  ];
  
  timeline.forEach(item => {
    const lines = doc.splitTextToSize('• ' + item, maxWidth);
    doc.text(lines, margin, y);
    y += lines.length * lineHeight;
  });
  y += 3;
  
  // Budget Overview
  doc.setFont('helvetica', 'bold');
  doc.text('BUDGET OVERVIEW', margin, y);
  y += lineHeight;
  doc.setFont('helvetica', 'normal');
  
  const budgetItems = [
    ['Personnel (Mestre, musicians, dancers, craftsmen)', '$45,000'],
    ['Video Production & Documentation', '$15,000'],
    ['Equipment & Instruments', '$12,000'],
    ['Venue & Production Costs', '$10,000'],
    ['Educational Platform Development', '$8,000'],
    ['Travel & Logistics', '$5,000'],
    ['Marketing & Outreach', '$3,000'],
    ['Contingency (5%)', '$2,000']
  ];
  
  budgetItems.forEach(item => {
    doc.text(item[0], margin, y);
    doc.text(item[1], pageWidth - margin - 25, y);
    y += lineHeight - 1;
  });
  
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL PROJECT BUDGET', margin, y);
  doc.text('$100,000', pageWidth - margin - 25, y);
  y += lineHeight + 3;
  
  // Key Deliverables
  doc.setFont('helvetica', 'bold');
  doc.text('KEY DELIVERABLES', margin, y);
  y += lineHeight;
  doc.setFont('helvetica', 'normal');
  
  const deliverables = [
    '5 professional video documentaries on ancestral instrument construction',
    'Educational curriculum for schools and universities',
    '2-5 live performances with orchestra (based on funding level)',
    'Online platform with free educational resources',
    'Network of 10+ traditional craftsmen contributing content'
  ];
  
  deliverables.forEach(item => {
    doc.text('• ' + item, margin, y);
    y += lineHeight - 1;
  });
  y += 3;
  
  // Contact
  doc.setFont('helvetica', 'bold');
  doc.text('CONTACT', margin, y);
  y += lineHeight;
  doc.setFont('helvetica', 'normal');
  doc.text('Mestre Curtis Pierre | therealsambaman@yahoo.com | casasamba.com/legacy', margin, y);
  
  doc.save('Ancestral_Strings_Action_Plan.pdf');
};

const downloadPDF = (content) => {
  if (!content) {
    alert('No content available to download');
    return;
  }
  
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - (margin * 2);
  
  // Title
  doc.setFontSize(18);
  doc.setTextColor(184, 134, 11); // Gold color
  doc.text('The Ancestral String Instruments', pageWidth / 2, 25, { align: 'center' });
  doc.text('of the Orchestra', pageWidth / 2, 33, { align: 'center' });
  
  // Subtitle
  doc.setFontSize(12);
  doc.setTextColor(100, 100, 100);
  doc.text('Funding Proposal', pageWidth / 2, 42, { align: 'center' });
  
  // Line
  doc.setDrawColor(184, 134, 11);
  doc.setLineWidth(0.5);
  doc.line(margin, 48, pageWidth - margin, 48);
  
  // Content
  doc.setFontSize(11);
  doc.setTextColor(51, 51, 51);
  const lines = doc.splitTextToSize(content, maxWidth);
  
  let y = 58;
  const lineHeight = 6;
  const pageHeight = doc.internal.pageSize.getHeight();
  
  lines.forEach((line) => {
    if (y > pageHeight - 30) {
      doc.addPage();
      y = 20;
    }
    doc.text(line, margin, y);
    y += lineHeight;
  });
  
  // Contact section on last page
  y += 10;
  if (y > pageHeight - 50) {
    doc.addPage();
    y = 20;
  }
  
  doc.setFontSize(12);
  doc.setTextColor(184, 134, 11);
  doc.text('CONTACT', margin, y);
  y += 8;
  
  doc.setFontSize(10);
  doc.setTextColor(51, 51, 51);
  doc.text('Mestre Curtis Pierre', margin, y); y += 5;
  doc.text('Escola de Samba Casa Samba', margin, y); y += 5;
  doc.text('Email: therealsambaman@yahoo.com', margin, y); y += 5;
  doc.text('Alt: mestre@casasamba.com', margin, y); y += 5;
  doc.text('Website: casasamba.com/legacy', margin, y);
  
  doc.save('Ancestral_Strings_Proposal.pdf');
};

// Budget PDF Download function
const downloadBudgetPDF = () => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  
  // Title
  doc.setFontSize(16);
  doc.setTextColor(184, 134, 11);
  doc.text('The Ancestral String Instruments of the Orchestra', pageWidth / 2, 20, { align: 'center' });
  
  doc.setFontSize(11);
  doc.setTextColor(100, 100, 100);
  doc.text('Performance Budget Proposals | New Orleans Area', pageWidth / 2, 28, { align: 'center' });
  doc.text('Prepared by Escola de Samba Casa Samba / Mestre Curtis Pierre', pageWidth / 2, 34, { align: 'center' });
  
  // Line
  doc.setDrawColor(184, 134, 11);
  doc.setLineWidth(0.5);
  doc.line(margin, 40, pageWidth - margin, 40);
  
  let y = 50;
  
  // Budget Overview
  doc.setFontSize(12);
  doc.setTextColor(44, 85, 48);
  doc.text('BUDGET OVERVIEW', margin, y);
  y += 8;
  
  doc.setFontSize(9);
  doc.setTextColor(51, 51, 51);
  
  // Overview table
  const overviewData = [
    ['Budget', 'Performances', 'Orchestra', 'Casa Samba'],
    ['$100,000', '5', '18 musicians', '15 members'],
    ['$75,000', '4', '14 musicians', '15 members'],
    ['$50,000', '2', '10 musicians', '12 members'],
    ['$25,000', '1', '8-10 musicians', '9 members']
  ];
  
  overviewData.forEach((row, i) => {
    const colWidth = 45;
    row.forEach((cell, j) => {
      if (i === 0) {
        doc.setFontSize(9);
        doc.setTextColor(255, 255, 255);
        doc.setFillColor(184, 134, 11);
        doc.rect(margin + (j * colWidth), y - 4, colWidth - 2, 7, 'F');
      } else {
        doc.setTextColor(51, 51, 51);
      }
      doc.text(cell, margin + (j * colWidth) + 2, y);
    });
    y += 8;
  });
  
  y += 5;
  
  // $100K Budget
  doc.setFontSize(11);
  doc.setFillColor(184, 134, 11);
  doc.rect(margin, y, pageWidth - (margin * 2), 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text('$100,000 BUDGET (Premium Package)', pageWidth / 2, y + 5.5, { align: 'center' });
  y += 14;
  
  doc.setFontSize(9);
  doc.setTextColor(51, 51, 51);
  const budget100k = [
    ['Mestre Curtis Pierre (Legend Fee)', '$12,000'],
    ['Orchestra Fees (18 musicians)', '$22,000'],
    ['Escola de Samba Casa Samba Ensemble (15 members)', '$18,000'],
    ['Food & Beverages', '$9,000'],
    ['Sound & Lighting', '$8,000'],
    ['Venue Expenses', '$10,000'],
    ['Audio Recording/Mixing', '$6,000'],
    ['Video Production', '$8,000'],
    ['Promotional & Contingency', '$7,000'],
    ['TOTAL', '$100,000']
  ];
  
  budget100k.forEach((row, i) => {
    if (i === budget100k.length - 1) {
      doc.setFontSize(10);
      doc.setTextColor(184, 134, 11);
    }
    doc.text(row[0], margin, y);
    doc.text(row[1], pageWidth - margin - 20, y);
    y += 5;
  });
  
  y += 5;
  
  // $75K Budget
  doc.setFontSize(11);
  doc.setFillColor(184, 134, 11);
  doc.rect(margin, y, pageWidth - (margin * 2), 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text('$75,000 BUDGET (Standard Package)', pageWidth / 2, y + 5.5, { align: 'center' });
  y += 14;
  
  doc.setFontSize(9);
  doc.setTextColor(51, 51, 51);
  const budget75k = [
    ['Mestre Curtis Pierre', '$9,000'],
    ['Orchestra Fees (14 musicians)', '$16,100'],
    ['Escola de Samba Casa Samba Ensemble', '$14,000'],
    ['Production & Venue', '$28,150'],
    ['Contingency', '$7,750'],
    ['TOTAL', '$75,000']
  ];
  
  budget75k.forEach((row, i) => {
    if (i === budget75k.length - 1) {
      doc.setFontSize(10);
      doc.setTextColor(184, 134, 11);
    }
    doc.text(row[0], margin, y);
    doc.text(row[1], pageWidth - margin - 20, y);
    y += 5;
  });
  
  // Page 2
  doc.addPage();
  y = 20;
  
  // $50K Budget
  doc.setFontSize(11);
  doc.setFillColor(184, 134, 11);
  doc.rect(margin, y, pageWidth - (margin * 2), 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text('$50,000 BUDGET (Essential Package)', pageWidth / 2, y + 5.5, { align: 'center' });
  y += 14;
  
  doc.setFontSize(9);
  doc.setTextColor(51, 51, 51);
  const budget50k = [
    ['Mestre Curtis Pierre', '$6,000'],
    ['Orchestra & Ensemble', '$18,000'],
    ['Production & Venue', '$21,000'],
    ['Contingency', '$5,000'],
    ['TOTAL', '$50,000']
  ];
  
  budget50k.forEach((row, i) => {
    if (i === budget50k.length - 1) {
      doc.setFontSize(10);
      doc.setTextColor(184, 134, 11);
    }
    doc.text(row[0], margin, y);
    doc.text(row[1], pageWidth - margin - 20, y);
    y += 5;
  });
  
  y += 10;
  
  // $25K Budget
  doc.setFontSize(11);
  doc.setFillColor(184, 134, 11);
  doc.rect(margin, y, pageWidth - (margin * 2), 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text('$25,000 BUDGET (Introductory Package)', pageWidth / 2, y + 5.5, { align: 'center' });
  y += 14;
  
  doc.setFontSize(9);
  doc.setTextColor(51, 51, 51);
  const budget25k = [
    ['Mestre Curtis Pierre', '$3,500'],
    ['Orchestra & Ensemble', '$9,400'],
    ['Production & Venue', '$10,600'],
    ['Contingency', '$1,500'],
    ['TOTAL', '$25,000']
  ];
  
  budget25k.forEach((row, i) => {
    if (i === budget25k.length - 1) {
      doc.setFontSize(10);
      doc.setTextColor(184, 134, 11);
    }
    doc.text(row[0], margin, y);
    doc.text(row[1], pageWidth - margin - 20, y);
    y += 5;
  });
  
  y += 10;
  
  // What's Included
  doc.setFontSize(11);
  doc.setTextColor(44, 85, 48);
  doc.text("WHAT'S INCLUDED IN EVERY PACKAGE", margin, y);
  y += 8;
  
  doc.setFontSize(9);
  doc.setTextColor(51, 51, 51);
  const included = [
    'Mestre Curtis Pierre - Living Legend, Featured Artist & Artistic Director',
    'Ancestral Instrument Demonstrations - Kora, Balafon, Ngoni, and more',
    'Escola de Samba Casa Samba Ensemble - Musicians & Dancers',
    'Professional Orchestra - String section with ancestral connections',
    'Educational Narration - Historical context & cultural significance',
    'Professional Sound & Lighting',
    'Audio/Video Documentation',
    'Food & Beverages for all personnel'
  ];
  
  included.forEach(item => {
    doc.text('• ' + item, margin, y);
    y += 5;
  });
  
  y += 10;
  
  // Contact
  doc.setFontSize(11);
  doc.setTextColor(184, 134, 11);
  doc.text('CONTACT', margin, y);
  y += 7;
  
  doc.setFontSize(9);
  doc.setTextColor(51, 51, 51);
  doc.text('Mestre Curtis Pierre', margin, y); y += 5;
  doc.text('Escola de Samba Casa Samba', margin, y); y += 5;
  doc.text('Email: therealsambaman@yahoo.com', margin, y); y += 5;
  doc.text('Alt: mestre@casasamba.com', margin, y); y += 5;
  doc.text('Website: casasamba.com/legacy', margin, y);
  
  doc.save('Ancestral_Strings_Budget.pdf');
};
// Icon mapping
const IconMap = {
  Crown, Globe, BookOpen, Trophy, Star, Award, Flame, Zap,
  Calendar, Users, Heart, Music, Drum, GraduationCap, Sparkles, Film, Clapperboard
};

// Animated glow text component
const GlowText = ({ children, className = "", delay = 0 }) => (
  <motion.span
    initial={{ opacity: 0, textShadow: "0 0 0px rgba(234, 179, 8, 0)" }}
    animate={{ 
      opacity: 1, 
      textShadow: [
        "0 0 20px rgba(234, 179, 8, 0.5)",
        "0 0 40px rgba(234, 179, 8, 0.8)",
        "0 0 20px rgba(234, 179, 8, 0.5)"
      ]
    }}
    transition={{ 
      delay, 
      duration: 2,
      textShadow: { repeat: Infinity, duration: 3 }
    }}
    className={className}
  >
    {children}
  </motion.span>
);

// Dramatic reveal wrapper
const DramaticReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 100, scale: 0.9 }}
      animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
      transition={{ 
        duration: 1,
        delay,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
    >
      {children}
    </motion.div>
  );
};

import { PageEditProvider } from '@/context/PageEditModeContext';
import { PageEditToolbar } from '@/components/edit/PageEditToolbar';
import { PageEditable } from '@/components/edit/PageEditable';
import { AdminEditorLink } from '@/components/edit/AdminEditorLink';

const LegacyInner = () => {
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [showFunderModal, setShowFunderModal] = useState(false);
  const [showMembershipPrompt, setShowMembershipPrompt] = useState(false);
  const [showRequestAccessModal, setShowRequestAccessModal] = useState(false);
  const [downloadAccess, setDownloadAccess] = useState({ has_access: false, status: 'none' });
  const [requestForm, setRequestForm] = useState({ name: '', email: '', organization: '', reason: '' });
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [content, setContent] = useState(null);
  const [koraSlideIndex, setKoraSlideIndex] = useState(0);
  const heroRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Check download access when user changes
  useEffect(() => {
    if (user?.email) {
      fetch(`${API_URL}/api/download-requests/check/${encodeURIComponent(user.email)}`)
        .then(res => res.json())
        .then(data => setDownloadAccess(data))
        .catch(() => setDownloadAccess({ has_access: false, status: 'none' }));
    }
  }, [user]);
  
  // Submit request for download access
  const submitAccessRequest = async () => {
    if (!requestForm.name || !requestForm.email || !requestForm.reason) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setSubmittingRequest(true);
    try {
      const res = await fetch(`${API_URL}/api/download-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...requestForm,
          document_type: 'all'
        })
      });
      const data = await res.json();
      
      if (data.success) {
        toast.success(data.message);
        setShowRequestAccessModal(false);
        setDownloadAccess({ has_access: false, status: 'pending' });
      } else {
        toast.error(data.message || 'Request failed');
      }
    } catch (err) {
      toast.error('Failed to submit request');
    } finally {
      setSubmittingRequest(false);
    }
  };
  
  // Protected download function - requires membership AND approved access
  const handleProtectedDownload = (downloadFn) => {
    if (!user) {
      setShowMembershipPrompt(true);
      return;
    }
    if (!downloadAccess.has_access) {
      setShowRequestAccessModal(true);
      setRequestForm(f => ({ ...f, name: user.name || '', email: user.email || '' }));
      return;
    }
    downloadFn();
  };
  
  // Making of Kora slideshow images
  const koraImages = [
    '/api/media/casa-samba/library/images/making-kora/kora_1_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_2_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_3_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_4_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_5_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_6_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_7_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_8_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_9_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_10_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_11_converted.jpg',
    '/api/media/casa-samba/library/images/making-kora/kora_12_converted.jpg',
  ];
  
  // Auto-advance slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setKoraSlideIndex((prev) => (prev + 1) % koraImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [koraImages.length]);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.2]);
  const heroY = useTransform(scrollYProgress, [0, 0.5], [0, 100]);

  useEffect(() => {
    // Fetch content from API
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API_URL}/api/about-editor`);
        const data = await response.json();
        setContent(data);
      } catch (error) {
        console.error('Error fetching about content:', error);
        // Use default content on error
        setContent(getDefaultContent());
      }
    };
    
    fetchContent();
    
    // Dramatic entrance delay
    const timer = setTimeout(() => setIsLoaded(true), 300);
    return () => clearTimeout(timer);
  }, []);

  // Default content fallback
  const getDefaultContent = () => ({
    hero: {
      preTitle: "THE LEGEND",
      mainTitle: "MESTRE",
      name: "CURTIS PIERRE",
      ambassadorTitle: "AMBASSADOR OF AFRO-BRAZILIAN CULTURE",
      subtitle: "Master of Two Worlds",
      badge1: "Master of Capoeira",
      badge2: "Master of Brasilian Percussion",
      description: "The ONLY AMERICAN in history to achieve Master level status in TWO distinct Brasilian cultural domains. A living legend dedicating 39+ years to bridging the African diaspora through rhythm, movement, and culture.",
      backgroundImage: "https://www.mestrecurtispierre.com/courses/wp-content/uploads/2020/04/1399.jpg"
    },
    achievementSection: {
      sectionLabel: "UNPRECEDENTED ACHIEVEMENT",
      title: "One of a Kind in the World",
      description: "Born in New Orleans, shaped by Brasil, Mestre Curtis Pierre bridges two worlds as the sole American to master both the ancient art of Capoeira and the soul-stirring rhythms of Brasilian percussion.",
      achievements: []
    },
    stats: { stats: [] },
    brazilianConnection: {
      sectionLabel: "CONEXÃO BRASILEIRA",
      title: "The Brasilian Connection",
      quote: "",
      quoteDescription: "",
      connections: []
    },
    journey: {
      sectionLabel: "THE JOURNEY",
      title: "From New Orleans to Brasil and Back",
      paragraphs: [],
      image: "",
      foundedYear: "1987",
      foundedText: "Escola de Samba Casa Samba Founded"
    },
    performance: {
      sectionLabel: "WORLD STAGE",
      title: "Performance Highlights",
      highlights: []
    },
    education: {
      sectionLabel: "GIVING BACK",
      title: "Educating the Next Generation",
      description: "",
      programs: [],
      ctaText: "Youth Programs",
      ctaLink: "https://www.thesambaman.org/"
    },
    newOrleans: {
      sectionLabel: "CULTURAL BRIDGE",
      title: "The New Orleans Connection",
      description: ""
    },
    cta: {
      title: "Experience the Legacy",
      description: "",
      button1Text: "View Classes",
      button1Link: "/classes",
      button2Text: "Online Courses",
      button2Link: "/courses"
    }
  });

  if (!content) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  const { hero, achievementSection, stats, studiedWith, filmCredits, brazilianConnection, journey, performance, education, newOrleans, cta } = content;

  return (
    <div className="min-h-screen bg-black overflow-hidden">
      {/* ========== EPIC CINEMATIC HERO ========== */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Multi-layer background with parallax */}
        <motion.div 
          className="absolute inset-0"
          style={{ scale: heroScale, y: heroY }}
        >
          <div 
            className="absolute inset-0 bg-cover bg-center scale-110" 
            style={{ backgroundImage: `url('${hero?.backgroundImage || "https://www.mestrecurtispierre.com/courses/wp-content/uploads/2020/04/1399.jpg"}')` }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/60 to-black" />
        </motion.div>

        {/* Animated golden rays */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute top-1/2 left-1/2 w-[200%] h-1"
              style={{ 
                background: `linear-gradient(90deg, transparent, rgba(234, 179, 8, ${0.1 + i * 0.02}), transparent)`,
                transform: `rotate(${i * 45}deg)`,
                transformOrigin: 'center'
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ 
                opacity: [0.3, 0.6, 0.3],
                scale: [1, 1.5, 1]
              }}
              transition={{ 
                duration: 4,
                delay: i * 0.2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
          ))}
        </div>

        {/* Floating particles/embers */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(40)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute rounded-full"
              style={{
                width: Math.random() * 4 + 2,
                height: Math.random() * 4 + 2,
                background: i % 3 === 0 
                  ? 'rgba(234, 179, 8, 0.8)' 
                  : i % 3 === 1 
                    ? 'rgba(251, 146, 60, 0.6)' 
                    : 'rgba(255, 255, 255, 0.4)',
                left: `${Math.random() * 100}%`,
                boxShadow: '0 0 10px currentColor'
              }}
              initial={{ y: '100vh', opacity: 0 }}
              animate={{ 
                y: '-100vh',
                opacity: [0, 1, 1, 0],
                x: [0, Math.random() * 100 - 50, 0]
              }}
              transition={{ 
                duration: 8 + Math.random() * 8,
                repeat: Infinity,
                delay: Math.random() * 10,
                ease: "linear"
              }}
            />
          ))}
        </div>

        {/* Vignette overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,black_70%)] pointer-events-none" />

        {/* Main hero content */}
        <motion.div 
          className="relative z-10 max-w-6xl mx-auto px-6 text-center"
          style={{ opacity: heroOpacity }}
        >
          <AnimatePresence>
            {isLoaded && (
              <>
                {/* Pre-title dramatic reveal */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.5, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="mb-8"
                >
                  <motion.div 
                    className="inline-flex items-center gap-4 mb-4"
                    initial={{ width: 0 }}
                    animate={{ width: "auto" }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                  >
                    <motion.div 
                      className="h-px bg-gradient-to-r from-transparent via-yellow-500 to-yellow-500"
                      initial={{ width: 0 }}
                      animate={{ width: 80 }}
                      transition={{ duration: 1, delay: 1 }}
                    />
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.5, delay: 1.2 }}
                    >
                      <Crown className="w-8 h-8 text-yellow-500" />
                    </motion.div>
                    <motion.div 
                      className="h-px bg-gradient-to-l from-transparent via-yellow-500 to-yellow-500"
                      initial={{ width: 0 }}
                      animate={{ width: 80 }}
                      transition={{ duration: 1, delay: 1 }}
                    />
                  </motion.div>

                  {/* THE LEGEND tag */}
                  <motion.span 
                    className="block text-yellow-500/90 font-bold tracking-[0.5em] text-xs sm:text-sm mb-2"
                    initial={{ opacity: 0, letterSpacing: "1em" }}
                    animate={{ opacity: 1, letterSpacing: "0.5em" }}
                    transition={{ duration: 1.5, delay: 0.8 }}
                  >
                    {hero?.preTitle || "THE LEGEND"}
                  </motion.span>
                </motion.div>

                {/* Main title with dramatic stagger */}
                <motion.h1
                  className="text-6xl sm:text-8xl md:text-9xl font-black text-white mb-2 tracking-tighter"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 1.5 }}
                >
                  {hero?.mainTitle || "MESTRE"}
                </motion.h1>

                <motion.h1
                  className="text-6xl sm:text-8xl md:text-9xl font-black mb-6 tracking-tighter"
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1, delay: 1.8 }}
                >
                  <GlowText 
                    className="bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 bg-clip-text text-transparent"
                    delay={2}
                  >
                    {hero?.name || "CURTIS PIERRE"}
                  </GlowText>
                </motion.h1>

                {/* Ambassador title */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8, y: 20 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 1, delay: 2.2 }}
                  className="mb-8"
                >
                  <div className="inline-block relative">
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-yellow-500/20 via-amber-500/30 to-orange-500/20 blur-xl"
                      animate={{ 
                        scale: [1, 1.2, 1],
                        opacity: [0.5, 0.8, 0.5]
                      }}
                      transition={{ duration: 3, repeat: Infinity }}
                    />
                    <div className="relative px-8 py-4 border-2 border-yellow-500/50 bg-black/50 backdrop-blur-sm">
                      <motion.span 
                        className="text-xl sm:text-2xl md:text-3xl font-bold tracking-wider"
                        style={{
                          background: 'linear-gradient(90deg, #fbbf24, #f59e0b, #fbbf24)',
                          backgroundSize: '200% 100%',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                        }}
                        animate={{
                          backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                        }}
                        transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
                      >
                        {hero?.ambassadorTitle || "AMBASSADOR OF AFRO-BRAZILIAN CULTURE"}
                      </motion.span>
                    </div>
                    {/* Corner accents */}
                    <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-yellow-500" />
                    <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-yellow-500" />
                    <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-yellow-500" />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-yellow-500" />
                  </div>
                </motion.div>

                {/* Subtitle */}
                {hero?.subtitle && (
                  <motion.p 
                    className="text-2xl md:text-3xl text-white/80 font-light italic mb-8"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.5 }}
                  >
                    {hero.subtitle}
                  </motion.p>
                )}

                {/* Mastery badges */}
                <motion.div 
                  className="flex flex-wrap justify-center gap-4 mb-10"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 2.8 }}
                >
                  <motion.span 
                    className="px-6 py-3 bg-gradient-to-r from-green-500/20 to-green-600/20 backdrop-blur-sm border border-green-500/50 rounded-full text-green-400 font-bold flex items-center gap-2"
                    whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(34, 197, 94, 0.3)" }}
                  >
                    <Flame className="w-4 h-4" />
                    {hero?.badge1 || "Master of Capoeira"}
                  </motion.span>
                  <motion.span 
                    className="px-6 py-3 bg-gradient-to-r from-amber-500/20 to-orange-500/20 backdrop-blur-sm border border-amber-500/50 rounded-full text-amber-400 font-bold flex items-center gap-2"
                    whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(245, 158, 11, 0.3)" }}
                  >
                    <Drum className="w-4 h-4" />
                    {hero?.badge2 || "Master of Brasilian Percussion"}
                  </motion.span>
                </motion.div>

                {/* Hero description */}
                <motion.div 
                  className="text-lg md:text-xl text-white/70 max-w-4xl mx-auto leading-relaxed mb-12 space-y-4"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 3 }}
                >
                  {/* Split long description into paragraphs */}
                  {(hero?.description || "").split(/\n\n+|\n/).filter(p => p.trim()).slice(0, 3).map((paragraph, idx) => (
                    <p 
                      key={idx}
                      className="text-lg md:text-xl leading-relaxed"
                      dangerouslySetInnerHTML={{ 
                        __html: paragraph.trim().replace(
                          /(ONLY AMERICAN|TWO|39\+ years)/g, 
                          '<span class="text-yellow-400 font-bold">$1</span>'
                        ) 
                      }}
                    />
                  ))}
                  {/* Show "Read More" if description is very long */}
                  {(hero?.description || "").length > 500 && (
                    <Link 
                      to="/mestre-full-story"
                      className="inline-flex items-center gap-2 text-yellow-400 hover:text-yellow-300 font-medium mt-2"
                    >
                      Read Full Biography <ChevronRight className="w-4 h-4" />
                    </Link>
                  )}
                </motion.div>

                {/* CTA Buttons */}
                <motion.div 
                  className="flex flex-wrap justify-center gap-6"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 3.2 }}
                >
                  <Link
                    to="/mestre-full-story"
                    className="group px-10 py-5 bg-gradient-to-r from-yellow-500 via-amber-500 to-orange-500 rounded-full text-black font-black text-lg flex items-center gap-3 relative overflow-hidden hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] transition-shadow"
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/30 to-white/0"
                      animate={{ x: ['-100%', '200%'] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 1 }}
                    />
                    <BookOpen className="w-6 h-6 relative z-10" />
                    <span className="relative z-10">Read Full Story</span>
                  </Link>
                  <motion.a
                    whileHover={{ scale: 1.05, borderColor: "rgba(245, 158, 11, 0.8)" }}
                    whileTap={{ scale: 0.95 }}
                    href="#legacy"
                    className="px-10 py-5 bg-white/5 backdrop-blur-sm border-2 border-white/30 rounded-full text-white font-bold text-lg flex items-center gap-3 transition-colors"
                  >
                    Discover the Legacy
                    <ChevronRight className="w-5 h-5" />
                  </motion.a>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 4 }}
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity }}
            className="flex flex-col items-center gap-2"
          >
            <span className="text-white/50 text-xs tracking-widest">SCROLL</span>
            <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
              <motion.div 
                className="w-1.5 h-3 bg-yellow-500 rounded-full"
                animate={{ y: [0, 8, 0], opacity: [1, 0.5, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              />
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ========== UNPRECEDENTED ACHIEVEMENT SECTION ========== */}
      <section className="py-32 bg-gradient-to-b from-black via-zinc-950 to-black relative" id="legacy">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(234,179,8,0.1),transparent_50%)]" />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <DramaticReveal>
            <div className="text-center mb-20">
              <motion.div
                initial={{ scale: 0 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 mb-6"
              >
                <Zap className="w-8 h-8 text-yellow-500" />
              </motion.div>
              <span className="block text-yellow-500 font-bold tracking-[0.3em] text-sm mb-4">{achievementSection?.sectionLabel}</span>
              <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                {(achievementSection?.title || "One of a Kind in the World").split(' ').map((word, i, arr) => 
                  i >= arr.length - 3 ? <span key={i} className="text-yellow-500">{word} </span> : <span key={i}>{word} </span>
                )}
              </h2>
              
              {/* FUND A LIVING LEGEND - Major CTA Banner */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="mb-8"
              >
                <button
                  onClick={() => setShowFunderModal(true)}
                  className="group relative w-full max-w-2xl mx-auto block overflow-hidden rounded-2xl"
                >
                  {/* Animated gradient background */}
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-500 via-yellow-500 to-orange-500 animate-pulse" />
                  <div className="absolute inset-0 bg-gradient-to-r from-amber-600/50 via-transparent to-orange-600/50 group-hover:opacity-0 transition-opacity" />
                  
                  {/* Sparkle effects */}
                  <div className="absolute top-2 left-4 w-2 h-2 bg-white rounded-full animate-ping" />
                  <div className="absolute top-4 right-8 w-1.5 h-1.5 bg-white rounded-full animate-ping delay-300" />
                  <div className="absolute bottom-3 left-12 w-1 h-1 bg-white rounded-full animate-ping delay-500" />
                  
                  <div className="relative px-8 py-6 text-center">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <Sparkles className="w-8 h-8 text-black animate-bounce" />
                      <span className="text-3xl md:text-4xl font-black text-black tracking-tight">
                        FUND A LIVING LEGEND
                      </span>
                      <Sparkles className="w-8 h-8 text-black animate-bounce" />
                    </div>
                    <p className="text-lg md:text-xl font-bold text-black/80">
                      The Ancestral String Instruments of the Orchestra
                    </p>
                    <div className="mt-3 inline-flex items-center gap-2 px-4 py-2 bg-black/20 rounded-full text-black font-bold text-sm group-hover:bg-black/30 transition-colors">
                      <Crown className="w-4 h-4" />
                      Click to Learn More & Partner With Us
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              </motion.div>
              
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed">
                {achievementSection?.description}
              </p>
            </div>
          </DramaticReveal>

          <div className="grid md:grid-cols-2 gap-8">
            {achievementSection?.achievements?.map((achievement, index) => {
              const Icon = IconMap[achievement.icon] || Star;
              return (
                <DramaticReveal key={index} delay={index * 0.15}>
                  <motion.div
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="group relative bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 rounded-2xl p-8 overflow-hidden"
                  >
                    <div className={`absolute inset-0 bg-gradient-to-br ${achievement.glow || 'from-yellow-500/30'} to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    
                    <div className="relative z-10 flex items-start gap-6">
                      <motion.div 
                        className="w-16 h-16 rounded-xl bg-gradient-to-br from-yellow-500/20 to-amber-600/20 border border-yellow-500/30 flex items-center justify-center flex-shrink-0"
                        whileHover={{ rotate: [0, -10, 10, 0] }}
                        transition={{ duration: 0.5 }}
                      >
                        <Icon className="w-8 h-8 text-yellow-500" />
                      </motion.div>
                      <div>
                        <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-yellow-400 transition-colors">{achievement.title}</h3>
                        <p className="text-zinc-400 leading-relaxed">{achievement.description}</p>
                      </div>
                    </div>
                  </motion.div>
                </DramaticReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ========== ANCESTRAL INSTRUMENTS HERO SLIDESHOW + VIDEO ========== */}
      <section className="py-20 relative overflow-hidden bg-gradient-to-b from-black via-zinc-900 to-black">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-amber-500/10 via-transparent to-transparent" />
        
        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <DramaticReveal>
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 rounded-full border border-amber-500/30 mb-4">
                <Video className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 text-sm font-bold tracking-wider">FEATURED PROJECT</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-4">
                The Ancestral String Instruments <span className="text-amber-400">of the Orchestra</span>
              </h2>
              <p className="text-zinc-400 max-w-2xl mx-auto text-lg">
                Discover the African origins of orchestral instruments through handcrafted masterpieces that are simultaneously Visual Art, Audio Instruments, and Performance Artifacts.
              </p>
            </div>
          </DramaticReveal>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Making of the Kora Slideshow */}
            <DramaticReveal delay={0.1}>
              <div className="bg-gradient-to-br from-zinc-900 to-black rounded-2xl border border-amber-500/20 overflow-hidden shadow-2xl">
                <div className="p-4 border-b border-amber-500/10">
                  <h3 className="text-lg font-bold text-amber-400 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" />
                    The Making of the Kora
                  </h3>
                  <p className="text-sm text-zinc-500">Handcrafted by Mestre Curtis Pierre</p>
                </div>
                <div className="relative aspect-square overflow-hidden">
                  <AnimatePresence mode="wait">
                    <motion.img
                      key={koraSlideIndex}
                      src={koraImages[koraSlideIndex]}
                      alt={`Making of the Kora - Step ${koraSlideIndex + 1}`}
                      className="w-full h-full object-cover"
                      initial={{ opacity: 0, scale: 1.1 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.7 }}
                    />
                  </AnimatePresence>
                  {/* Slide indicators */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                    {koraImages.map((_, idx) => (
                      <button
                        key={idx}
                        onClick={() => setKoraSlideIndex(idx)}
                        className={`w-2 h-2 rounded-full transition-all ${idx === koraSlideIndex ? 'bg-amber-400 w-6' : 'bg-white/30 hover:bg-white/50'}`}
                      />
                    ))}
                  </div>
                  {/* Slide counter */}
                  <div className="absolute top-4 right-4 px-3 py-1 bg-black/60 rounded-full text-amber-400 text-sm font-bold">
                    {koraSlideIndex + 1} / {koraImages.length}
                  </div>
                </div>
              </div>
            </DramaticReveal>
            
            {/* Video Player */}
            <DramaticReveal delay={0.2}>
              <div className="bg-gradient-to-br from-zinc-900 to-black rounded-2xl border border-amber-500/20 overflow-hidden shadow-2xl flex flex-col">
                <div className="p-4 border-b border-amber-500/10">
                  <h3 className="text-lg font-bold text-amber-400 flex items-center gap-2">
                    <Play className="w-5 h-5" />
                    Watch the Teaser Video
                  </h3>
                  <p className="text-sm text-zinc-500">Triple-Threat Art Pieces: Visual Art • Audio • Performance</p>
                </div>
                <div className="relative aspect-video flex-grow">
                  <video 
                    controls 
                    className="w-full h-full object-contain bg-black"
                    preload="auto"
                    playsInline
                  >
                    <source src="/videos/ancestral_strings_complete_teaser.mp4?v=7" type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="p-4 border-t border-amber-500/10">
                  <button
                    onClick={() => setShowFunderModal(true)}
                    className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold rounded-xl transition-all"
                  >
                    <Sparkles className="w-5 h-5" />
                    Learn More & Funding Info
                  </button>
                </div>
              </div>
            </DramaticReveal>
          </div>
        </div>
      </section>

      {/* ========== STATS SECTION ========== */}
      {stats?.stats?.length > 0 && (
        <section className="py-20 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-yellow-500/5 via-amber-500/10 to-orange-500/5" />
          
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.stats.map((stat, index) => {
                const Icon = IconMap[stat.icon] || Star;
                return (
                  <DramaticReveal key={index} delay={index * 0.1}>
                    <div className="text-center group">
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      >
                        <Icon className="w-10 h-10 text-yellow-500 mx-auto mb-4" />
                      </motion.div>
                      <motion.div 
                        className="text-5xl md:text-6xl font-black text-white mb-2"
                        initial={{ opacity: 0, scale: 0.5 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 + index * 0.1 }}
                      >
                        {stat.value}
                      </motion.div>
                      <div className="text-zinc-400 font-medium tracking-wide">{stat.label}</div>
                    </div>
                  </DramaticReveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ========== STUDIED WITH THE MASTERS SECTION ========== */}
      {studiedWith?.masters?.length > 0 && (
        <section className="py-32 bg-gradient-to-b from-black to-zinc-950 relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(234,179,8,0.08),transparent_50%)]" />
          
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <DramaticReveal>
              <div className="text-center mb-16">
                <span className="text-yellow-500 font-bold tracking-[0.3em] text-sm mb-4 block">{studiedWith?.sectionLabel || "TRAINED BY LEGENDS"}</span>
                <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                  {(studiedWith?.title || "Studied With the Masters").split(' ').slice(0, -2).join(' ')} <span className="text-yellow-500">{(studiedWith?.title || "Studied With the Masters").split(' ').slice(-2).join(' ')}</span>
                </h2>
                <p className="text-xl text-zinc-400 max-w-3xl mx-auto">
                  {studiedWith?.description}
                </p>
              </div>
            </DramaticReveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {studiedWith.masters.map((master, index) => (
                <DramaticReveal key={index} delay={index * 0.1}>
                  <motion.div
                    whileHover={{ scale: 1.02, y: -5 }}
                    className="bg-zinc-900/50 backdrop-blur-sm border border-zinc-800 hover:border-yellow-500/30 rounded-2xl p-6 transition-all h-full"
                  >
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-yellow-500/30 to-amber-500/30 flex items-center justify-center">
                        <Star className="w-7 h-7 text-yellow-500" />
                      </div>
                      <div>
                        <h4 className="font-bold text-white text-lg">{master.name}</h4>
                        <p className="text-sm text-yellow-500/80">{master.title}</p>
                        {master.years && <p className="text-xs text-zinc-500">{master.years}</p>}
                      </div>
                    </div>
                    {master.description && (
                      <p className="text-zinc-400 text-sm leading-relaxed line-clamp-4">{master.description.substring(0, 300)}{master.description.length > 300 ? '...' : ''}</p>
                    )}
                  </motion.div>
                </DramaticReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== FILM CREDITS SECTION ========== */}
      {filmCredits?.credits?.length > 0 && (
        <section className="py-32 bg-zinc-950 relative overflow-hidden">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(168,85,247,0.08),transparent_60%)]" />
          
          {/* Film reel decoration */}
          <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-2 bg-gradient-to-r from-transparent via-purple-500/20 to-transparent" />
          
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <DramaticReveal>
              <div className="text-center mb-16">
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 mb-6"
                >
                  <Film className="w-8 h-8 text-purple-400" />
                </motion.div>
                <span className="block text-purple-400 font-bold tracking-[0.3em] text-sm mb-4">{filmCredits?.sectionLabel || "HOLLYWOOD"}</span>
                <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                  {(filmCredits?.title || "Film & Television").split(' ').slice(0, -1).join(' ')} <span className="text-purple-400">{(filmCredits?.title || "Film & Television").split(' ').slice(-1)}</span>
                </h2>
                <p className="text-xl text-zinc-400 max-w-3xl mx-auto">
                  {filmCredits?.description}
                </p>
              </div>
            </DramaticReveal>

            <div className="grid md:grid-cols-2 gap-8">
              {filmCredits.credits.map((credit, index) => (
                <DramaticReveal key={index} delay={index * 0.15}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className={`relative rounded-2xl p-8 overflow-hidden ${
                      credit.highlight 
                        ? 'bg-gradient-to-br from-purple-500/20 via-zinc-900/80 to-pink-500/20 border-2 border-purple-500/50' 
                        : 'bg-zinc-900/50 border border-zinc-800'
                    }`}
                  >
                    {credit.highlight && (
                      <motion.div
                        className="absolute top-4 right-4 px-3 py-1 bg-purple-500/30 rounded-full text-xs text-purple-300 font-bold"
                        animate={{ opacity: [1, 0.6, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        FEATURED
                      </motion.div>
                    )}
                    
                    <div className="flex items-start gap-5">
                      <div className={`w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        credit.highlight 
                          ? 'bg-gradient-to-br from-purple-500/40 to-pink-500/40' 
                          : 'bg-zinc-800'
                      }`}>
                        <Clapperboard className={`w-8 h-8 ${credit.highlight ? 'text-purple-300' : 'text-zinc-400'}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className={`text-2xl font-bold ${credit.highlight ? 'text-purple-300' : 'text-white'}`}>
                            {credit.title}
                          </h3>
                          {credit.year && (
                            <span className="px-2 py-1 bg-zinc-800 rounded text-xs text-zinc-400 font-mono">
                              {credit.year}
                            </span>
                          )}
                        </div>
                        <p className={`text-sm font-semibold mb-3 ${credit.highlight ? 'text-purple-400' : 'text-yellow-500'}`}>
                          {credit.role}
                        </p>
                        <p className="text-zinc-400 leading-relaxed">
                          {credit.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </DramaticReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== BRAZILIAN CONNECTIONS SECTION ========== */}
      {brazilianConnection?.connections?.length > 0 && (
        <section className="py-32 bg-gradient-to-b from-black to-zinc-950 relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(234,179,8,0.05),transparent_60%)]" />
          
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <DramaticReveal>
              <div className="text-center mb-16">
                <span className="text-green-500 font-bold tracking-[0.3em] text-sm mb-4 block">{brazilianConnection?.sectionLabel}</span>
                <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                  {(brazilianConnection?.title || "").split(' ').slice(0, -1).join(' ')} <span className="text-green-500">{(brazilianConnection?.title || "").split(' ').slice(-1)}</span>
                </h2>
                {brazilianConnection?.quote && (
                  <p className="text-xl text-zinc-400 max-w-3xl mx-auto leading-relaxed italic">
                    "{brazilianConnection.quote}"
                  </p>
                )}
                {brazilianConnection?.quoteDescription && (
                  <p className="text-lg text-zinc-500 mt-4">
                    {brazilianConnection.quoteDescription}
                  </p>
                )}
              </div>
            </DramaticReveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {brazilianConnection.connections.map((connection, index) => (
                <DramaticReveal key={index} delay={index * 0.08}>
                  <motion.div
                    whileHover={{ scale: 1.03, x: 5 }}
                    className={`relative p-5 rounded-xl border transition-all ${
                      connection.highlight 
                        ? 'bg-gradient-to-r from-green-500/20 to-yellow-500/20 border-green-500/50' 
                        : 'bg-zinc-900/50 border-zinc-800 hover:border-green-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                        connection.highlight 
                          ? 'bg-green-500/30' 
                          : 'bg-zinc-800'
                      }`}>
                        <Star className={`w-6 h-6 ${connection.highlight ? 'text-green-400' : 'text-yellow-500'}`} />
                      </div>
                      <div>
                        <h4 className={`font-bold ${connection.highlight ? 'text-green-400' : 'text-white'}`}>
                          {connection.name}
                        </h4>
                        <p className="text-sm text-zinc-500">{connection.role}</p>
                      </div>
                    </div>
                    {connection.highlight && (
                      <motion.div
                        className="absolute top-2 right-2 px-2 py-1 bg-green-500/20 rounded text-xs text-green-400 font-bold"
                        animate={{ opacity: [1, 0.5, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        NEW 2024
                      </motion.div>
                    )}
                  </motion.div>
                </DramaticReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== THE JOURNEY SECTION ========== */}
      {journey?.paragraphs?.length > 0 && (
        <section className="py-32 bg-zinc-950 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-yellow-500/5 to-transparent" />
          
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <DramaticReveal>
                <div>
                  <span className="text-yellow-500 font-bold tracking-[0.3em] text-sm mb-4 block">{journey?.sectionLabel}</span>
                  <h2 className="text-4xl md:text-5xl font-black text-white mb-8">
                    {(journey?.title || "").split(' ').slice(0, 3).join(' ')} <br/><span className="text-yellow-500">{(journey?.title || "").split(' ').slice(3).join(' ')}</span>
                  </h2>
                  
                  {/* Show only first 2 paragraphs as preview */}
                  <div className="space-y-6 text-lg text-zinc-400 leading-relaxed">
                    {journey.paragraphs.slice(0, 2).map((paragraph, index) => (
                      <p key={index} dangerouslySetInnerHTML={{ 
                        __html: paragraph
                          .replace(/New Orleans/g, '<strong class="text-white">New Orleans</strong>')
                          .replace(/Brasil/g, '<strong class="text-white">Brasil</strong>')
                          .replace(/Casa Samba/g, '<strong class="text-yellow-400">Casa Samba</strong>')
                          .replace(/"Mestre"/g, '<strong class="text-yellow-400">"Mestre"</strong>')
                      }} />
                    ))}
                  </div>
                  
                  {/* Read Full Story Button */}
                  {journey.paragraphs.length > 2 && (
                    <Link 
                      to="/mestre-full-story"
                      className="inline-flex items-center gap-2 mt-8 px-6 py-3 bg-gradient-to-r from-yellow-500 to-amber-500 text-black font-bold rounded-xl hover:from-yellow-400 hover:to-amber-400 transition-all shadow-lg shadow-yellow-500/20"
                    >
                      <BookOpen className="w-5 h-5" />
                      Read Full Story
                      <ChevronRight className="w-5 h-5" />
                    </Link>
                  )}
                </div>
              </DramaticReveal>

              {journey?.image && (
                <DramaticReveal delay={0.3}>
                  <div className="relative">
                    <motion.div
                      className="absolute -inset-4 bg-gradient-to-r from-yellow-500/20 via-amber-500/20 to-orange-500/20 rounded-3xl blur-xl"
                      animate={{ opacity: [0.3, 0.6, 0.3] }}
                      transition={{ duration: 4, repeat: Infinity }}
                    />
                    <div className="relative aspect-square rounded-3xl overflow-hidden border-2 border-yellow-500/20">
                      <img
                        src={journey.image}
                        alt="Mestre Curtis Pierre"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    </div>
                    <motion.div 
                      className="absolute -bottom-6 -right-6 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-2xl p-6 text-black shadow-2xl"
                      whileHover={{ scale: 1.05 }}
                    >
                      <p className="text-4xl font-black">{journey?.foundedYear}</p>
                      <p className="text-sm font-bold">{journey?.foundedText}</p>
                    </motion.div>
                  </div>
                </DramaticReveal>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ========== PERFORMANCE HIGHLIGHTS ========== */}
      {performance?.highlights?.length > 0 && (
        <section className="py-32 bg-gradient-to-b from-zinc-950 to-black relative">
          <div className="max-w-6xl mx-auto px-6">
            <DramaticReveal>
              <div className="text-center mb-16">
                <span className="text-amber-500 font-bold tracking-[0.3em] text-sm mb-4 block">{performance?.sectionLabel}</span>
                <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                  {(performance?.title || "").split(' ').slice(0, -1).join(' ')} <span className="text-amber-500">{(performance?.title || "").split(' ').slice(-1)}</span>
                </h2>
              </div>
            </DramaticReveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {performance.highlights.map((highlight, index) => (
                <DramaticReveal key={index} delay={index * 0.05}>
                  <motion.div
                    whileHover={{ scale: 1.02, borderColor: 'rgba(245, 158, 11, 0.5)' }}
                    className="bg-zinc-900/30 backdrop-blur-sm border border-zinc-800 rounded-xl p-5 flex items-center gap-4"
                  >
                    <motion.div
                      whileHover={{ rotate: 360, scale: 1.2 }}
                      transition={{ duration: 0.5 }}
                    >
                      <Sparkles className="w-6 h-6 text-amber-500 flex-shrink-0" />
                    </motion.div>
                    <span className="text-zinc-300">{highlight}</span>
                  </motion.div>
                </DramaticReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== COMMUNITY & EDUCATION ========== */}
      {education?.programs?.length > 0 && (
        <section className="py-32 bg-black relative">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(34,197,94,0.05),transparent_60%)]" />
          
          <div className="max-w-6xl mx-auto px-6 relative z-10">
            <DramaticReveal>
              <div className="text-center mb-16">
                <span className="text-green-500 font-bold tracking-[0.3em] text-sm mb-4 block">{education?.sectionLabel}</span>
                <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                  {(education?.title || "").split(' ').slice(0, -2).join(' ')} <span className="text-green-500">{(education?.title || "").split(' ').slice(-2).join(' ')}</span>
                </h2>
                <p className="text-xl text-zinc-400 max-w-3xl mx-auto">
                  {education?.description}
                </p>
              </div>
            </DramaticReveal>

            <div className="grid md:grid-cols-2 gap-6">
              {education.programs.map((program, index) => (
                <DramaticReveal key={index} delay={index * 0.1}>
                  <motion.div
                    whileHover={{ scale: 1.02 }}
                    className="bg-zinc-900/50 border border-zinc-800 hover:border-green-500/30 rounded-2xl p-8 transition-all"
                  >
                    <div className="flex items-start gap-5">
                      <div className="w-14 h-14 rounded-xl bg-green-500/20 flex items-center justify-center flex-shrink-0">
                        <GraduationCap className="w-7 h-7 text-green-500" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{program.title}</h3>
                        <p className="text-zinc-400">{program.description}</p>
                      </div>
                    </div>
                  </motion.div>
                </DramaticReveal>
              ))}
            </div>

            {education?.ctaLink && (
              <DramaticReveal delay={0.4}>
                <div className="mt-12 text-center">
                  <motion.a
                    whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(34, 197, 94, 0.4)" }}
                    whileTap={{ scale: 0.95 }}
                    href={education.ctaLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full text-white font-bold text-lg"
                  >
                    <Heart className="w-6 h-6" />
                    {education?.ctaText || "Learn More"}
                  </motion.a>
                </div>
              </DramaticReveal>
            )}
          </div>
        </section>
      )}

      {/* ========== NEW ORLEANS CONNECTION ========== */}
      {newOrleans?.description && (
        <section className="py-32 bg-gradient-to-b from-black to-zinc-950 relative">
          <div className="max-w-6xl mx-auto px-6">
            <DramaticReveal>
              <div className="text-center mb-16">
                <span className="text-purple-500 font-bold tracking-[0.3em] text-sm mb-4 block">{newOrleans?.sectionLabel}</span>
                <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                  {(newOrleans?.title || "").split(' ').slice(0, -1).join(' ')} <span className="text-purple-500">{(newOrleans?.title || "").split(' ').slice(-1)}</span>
                </h2>
                <p className="text-xl text-zinc-400 max-w-3xl mx-auto">
                  {newOrleans?.description}
                </p>
              </div>
            </DramaticReveal>

            <DramaticReveal delay={0.2}>
              <div className="bg-gradient-to-r from-purple-500/10 via-zinc-900/50 to-amber-500/10 border border-purple-500/20 rounded-3xl p-10 md:p-14">
                <div className="grid md:grid-cols-3 gap-10 text-center">
                  <motion.div whileHover={{ y: -10 }}>
                    <Drum className="w-14 h-14 text-purple-500 mx-auto mb-5" />
                    <h4 className="text-2xl font-bold text-white mb-3">Carnival Traditions</h4>
                    <p className="text-zinc-400">The rhythms of Rio echo in the streets of New Orleans</p>
                  </motion.div>
                  <motion.div whileHover={{ y: -10 }}>
                    <Users className="w-14 h-14 text-amber-500 mx-auto mb-5" />
                    <h4 className="text-2xl font-bold text-white mb-3">Mardi Gras Indians</h4>
                    <p className="text-zinc-400">African heritage expressed through costume and dance</p>
                  </motion.div>
                  <motion.div whileHover={{ y: -10 }}>
                    <Music className="w-14 h-14 text-purple-500 mx-auto mb-5" />
                    <h4 className="text-2xl font-bold text-white mb-3">Second Line</h4>
                    <p className="text-zinc-400">The heartbeat of the city, rooted in African rhythm</p>
                  </motion.div>
                </div>
              </div>
            </DramaticReveal>
          </div>
        </section>
      )}

      {/* ========== FINAL CTA ========== */}
      <section className="py-32 bg-black relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-t from-yellow-500/10 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-yellow-500/50 to-transparent" />
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <DramaticReveal>
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
                {(cta?.title || "").split(' ').slice(0, -1).join(' ')} <span className="text-yellow-500">{(cta?.title || "").split(' ').slice(-1)}</span>
              </h2>
              <p className="text-xl text-zinc-400 mb-12 max-w-2xl mx-auto">
                {cta?.description}
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <motion.a
                  whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(245, 158, 11, 0.5)" }}
                  whileTap={{ scale: 0.95 }}
                  href={cta?.button1Link || "/classes"}
                  className="px-10 py-5 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full text-black font-black text-lg"
                >
                  {cta?.button1Text || "View Classes"}
                </motion.a>
                <motion.a
                  whileHover={{ scale: 1.05, borderColor: "rgba(245, 158, 11, 0.8)" }}
                  whileTap={{ scale: 0.95 }}
                  href={cta?.button2Link || "/courses"}
                  className="px-10 py-5 bg-white/5 border-2 border-white/30 rounded-full text-white font-bold text-lg transition-colors"
                >
                  {cta?.button2Text || "Online Courses"}
                </motion.a>
              </div>
              
              {/* Watch the Journey Video - Coming Soon */}
              <motion.div 
                className="mt-12 pt-8 border-t border-white/10"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
              >
                <p className="text-zinc-500 text-sm mb-4">Video Documentary Coming Soon</p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate('/gallery')}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-zinc-900 border border-zinc-700 rounded-full text-zinc-300 font-medium hover:border-yellow-500/50 hover:text-white transition-all"
                  data-testid="watch-journey-btn"
                >
                  <Play className="w-5 h-5" />
                  Explore the Journey in Photos
                </motion.button>
              </motion.div>
            </motion.div>
          </DramaticReveal>
        </div>
      </section>

      {/* ========== HIDDEN LINK TO MUSEUM PROPOSAL ========== */}
      <section className="max-w-6xl mx-auto px-6 py-6 text-center border-t border-white/5">
        <Link
          to="/museum-proposal"
          className="text-xs text-muted-foreground/50 hover:text-primary transition-colors inline-flex items-center gap-1"
          data-testid="museum-proposal-link"
        >
          Museum Proposal &middot; For Partners &amp; Grant Officers
        </Link>
      </section>

      {/* ========== VIDEO MODAL ========== */}
      <AnimatePresence>
        {showVideoModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-sm"
            onClick={() => setShowVideoModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", damping: 25 }}
              className="w-full max-w-5xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-end mb-4">
                <motion.button
                  whileHover={{ scale: 1.1, rotate: 90 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={() => setShowVideoModal(false)}
                  className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
                >
                  <X className="w-8 h-8 text-white" />
                </motion.button>
              </div>
              <div className="aspect-video bg-zinc-900 rounded-2xl overflow-hidden border border-yellow-500/20">
                <iframe
                  src="https://player.vimeo.com/video/trailer"
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen"
                  allowFullScreen
                  title="Mestre Curtis Pierre"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ========== FUNDER MODAL - Ancestral Strings Project ========== */}
      <AnimatePresence>
        {showFunderModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/95 backdrop-blur-xl overflow-y-auto"
            onClick={() => setShowFunderModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-b from-zinc-900 to-black rounded-3xl border border-yellow-500/20 shadow-2xl"
            >
              {/* Close button */}
              <button
                onClick={() => setShowFunderModal(false)}
                className="absolute top-4 right-4 z-10 p-2 bg-white/10 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-white" />
              </button>

              {/* Header with badge */}
              <div className="p-8 pb-0 text-center border-b border-yellow-500/10">
                <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
                  <div className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-500/10 rounded-full border border-yellow-500/30">
                    <Crown className="w-4 h-4 text-yellow-500" />
                    <span className="text-yellow-500 text-sm font-bold tracking-wider">FOR FUNDERS & PARTNERS</span>
                  </div>
                  {/* PDF Download Button - Protected */}
                  <button
                    onClick={() => handleProtectedDownload(() => downloadPDF(content?.funderPitch?.pdfSummary || ''))}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-full border border-green-500/30 transition-colors"
                    title={user ? "Download Project Summary PDF" : "Members only - Sign in to download"}
                  >
                    {!user && <Lock className="w-3 h-3 text-green-400" />}
                    <Download className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-sm font-bold">Project Summary</span>
                  </button>
                  {/* Budget PDF Download Button - Protected */}
                  <button
                    onClick={() => handleProtectedDownload(() => downloadBudgetPDF())}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 rounded-full border border-blue-500/30 transition-colors"
                    title={user ? "Download Budget Proposals PDF" : "Members only - Sign in to download"}
                  >
                    {!user && <Lock className="w-3 h-3 text-blue-400" />}
                    <FileText className="w-4 h-4 text-blue-400" />
                    <span className="text-blue-400 text-sm font-bold">Budget Proposals</span>
                  </button>
                  <button
                    onClick={() => handleProtectedDownload(() => downloadActionPlanPDF())}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-full border border-green-500/30 transition-colors"
                    title={user ? "Download Action Plan PDF" : "Members only - Sign in to download"}
                  >
                    {!user && <Lock className="w-3 h-3 text-green-400" />}
                    <FileText className="w-4 h-4 text-green-400" />
                    <span className="text-green-400 text-sm font-bold">Action Plan</span>
                  </button>
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-4">
                  <span className="text-yellow-500">The Ancestral String Instruments</span> of the Orchestra
                </h2>
                <p className="text-zinc-400 mb-6">{content?.funderPitch?.modalSubtitle || "A once-in-a-generation opportunity to reveal the African origins of orchestral instruments"}</p>
              </div>

              {/* PROMOTIONAL VIDEO SECTION */}
              <div className="p-6 border-b border-yellow-500/10">
                <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-2xl p-4 border border-amber-500/20">
                  <div className="flex items-center gap-2 mb-3">
                    <Video className="w-5 h-5 text-amber-400" />
                    <h3 className="text-lg font-bold text-amber-400">Watch: The Ancestral String Instruments</h3>
                  </div>
                  <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                    <video 
                      controls 
                      className="w-full h-full object-contain"
                      preload="auto"
                      playsInline
                    >
                      <source src="/videos/ancestral_strings_complete_teaser.mp4?v=7" type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <p className="text-sm text-zinc-400 mt-3 text-center">
                    Full teaser: Making of the Kora, Baloan, Kora, Omuko performances & the Kora with internal water fountain
                  </p>
                </div>
              </div>

              {/* Main content - The pitch */}
              <div className="p-8 space-y-6 text-zinc-300 leading-relaxed">
                
                {/* Opening Hook */}
                <div className="bg-yellow-500/5 border-l-4 border-yellow-500 p-6 rounded-r-xl">
                  <p className="text-lg text-white font-medium">
                    {content?.funderPitch?.openingHook || "What if you could fund the revelation that transforms how the world understands music?"}
                  </p>
                </div>

                {/* Section 1: Why One of a Kind */}
                <div>
                  <h3 className="text-xl font-bold text-yellow-500 mb-3 flex items-center gap-2">
                    <Star className="w-5 h-5" /> {content?.funderPitch?.whyOneOfAKindTitle || 'Why "One of a Kind in the World"?'}
                  </h3>
                  {(content?.funderPitch?.whyOneOfAKindContent || "").split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="mb-4">{paragraph}</p>
                  ))}
                </div>

                {/* Section 2: The Ancestral String Instruments Project */}
                <div>
                  <h3 className="text-xl font-bold text-yellow-500 mb-3 flex items-center gap-2">
                    <Globe className="w-5 h-5" /> {content?.funderPitch?.projectTitle || "The Ancestral String Instruments of the Orchestra"}
                  </h3>
                  {(content?.funderPitch?.projectDescription || "").split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="mb-4">{paragraph}</p>
                  ))}
                  <p className="mb-4">This project bridges this gap through:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                    {(content?.funderPitch?.projectBullets || []).map((bullet, idx) => (
                      <li key={idx}><strong className="text-white">{bullet.split(' ')[0]}</strong> {bullet.split(' ').slice(1).join(' ')}</li>
                    ))}
                  </ul>
                  <p>
                    {content?.funderPitch?.projectClosing || "The content is created. The curriculum is developed."}
                  </p>
                </div>

                {/* Section 2.5: Triple-Threat Art Pieces */}
                <div className="bg-gradient-to-br from-amber-500/10 via-zinc-900/50 to-purple-500/10 border border-amber-500/20 rounded-2xl p-6">
                  <h3 className="text-xl font-bold text-amber-400 mb-3 flex items-center gap-2">
                    <Sparkles className="w-5 h-5" /> {content?.funderPitch?.tripleThreatTitle || "Triple-Threat Art Pieces: Visual Art, Audio & Performance"}
                  </h3>
                  <p className="mb-4">
                    {content?.funderPitch?.tripleThreatIntro || "What makes ancestral string instruments truly extraordinary is their triple-threat nature."}
                  </p>
                  <div className="grid md:grid-cols-3 gap-4 mb-4">
                    <div className="bg-zinc-800/50 rounded-xl p-4 border border-amber-500/20">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mb-3">
                        <Star className="w-5 h-5 text-amber-400" />
                      </div>
                      <h4 className="font-bold text-white mb-2">1. Visual Arts</h4>
                      <p className="text-sm text-zinc-400">
                        {content?.funderPitch?.visualArtsDescription || "Each instrument is a handcrafted sculpture..."}
                      </p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-xl p-4 border border-amber-500/20">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mb-3">
                        <Music className="w-5 h-5 text-amber-400" />
                      </div>
                      <h4 className="font-bold text-white mb-2">2. Audio Instruments</h4>
                      <p className="text-sm text-zinc-400">
                        {content?.funderPitch?.audioDescription || "Despite their artistic complexity, these instruments produce authentic sounds..."}
                      </p>
                    </div>
                    <div className="bg-zinc-800/50 rounded-xl p-4 border border-amber-500/20">
                      <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center mb-3">
                        <Flame className="w-5 h-5 text-amber-400" />
                      </div>
                      <h4 className="font-bold text-white mb-2">3. Performance Artifacts</h4>
                      <p className="text-sm text-zinc-400">
                        {content?.funderPitch?.performanceDescription || "On stage, these instruments command attention..."}
                      </p>
                    </div>
                  </div>
                  <div className="bg-zinc-900/80 rounded-xl p-4 border-l-4 border-amber-500">
                    <p className="text-zinc-300 mb-2">
                      <strong className="text-amber-400">{content?.funderPitch?.koraExampleTitle || "Example: The Kora with an Internal Water Fountain"}</strong>
                    </p>
                    {(content?.funderPitch?.koraExampleContent || "").split('\n\n').map((paragraph, idx) => (
                      <p key={idx} className="text-sm text-zinc-400 mb-2">{paragraph}</p>
                    ))}
                  </div>
                  <p className="mt-4 text-zinc-300">
                    {content?.funderPitch?.tripleThreatClosing || "When funders invest in The Ancestral String Instruments of the Orchestra..."}
                  </p>
                </div>

                {/* Section 3: Impact & Legacy */}
                <div>
                  <h3 className="text-xl font-bold text-yellow-500 mb-3 flex items-center gap-2">
                    <Heart className="w-5 h-5" /> {content?.funderPitch?.culturalImpactTitle || "Cultural Impact & Urgency"}
                  </h3>
                  {(content?.funderPitch?.culturalImpactContent || "").split('\n\n').map((paragraph, idx) => (
                    <p key={idx} className="mb-4">{paragraph}</p>
                  ))}
                </div>

                {/* Call to Action */}
                <div className="bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-2xl p-6 text-center">
                  <h3 className="text-xl font-bold text-white mb-3">{content?.funderPitch?.ctaTitle || "Ready to Discuss Partnership?"}</h3>
                  <p className="text-zinc-300 mb-4">
                    {content?.funderPitch?.ctaDescription || "The Ancestral String Instruments of the Orchestra is seeking funding partners..."}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <a 
                      href={`mailto:${content?.funderPitch?.contactEmail || 'therealsambaman@yahoo.com'}?subject=Ancestral%20String%20Instruments%20Funding%20Inquiry`}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors"
                    >
                      <Award className="w-5 h-5" />
                      {content?.funderPitch?.contactButtonText || "Contact for Funding Discussion"}
                    </a>
                    <button
                      onClick={() => handleProtectedDownload(() => downloadPDF(content?.funderPitch?.pdfSummary || ''))}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-green-500/50 hover:bg-green-500/10 text-green-400 font-bold rounded-xl transition-colors"
                    >
                      {!user && <Lock className="w-4 h-4" />}
                      <Download className="w-5 h-5" />
                      {user ? 'Download PDF Summary' : 'Members Only'}
                    </button>
                  </div>
                </div>

                {/* Credentials summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-zinc-800">
                  <div className="text-center">
                    <div className="text-2xl font-black text-yellow-500">39+</div>
                    <div className="text-xs text-zinc-500">Years Dedicated</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-black text-yellow-500">2</div>
                    <div className="text-xs text-zinc-500">Master Titles</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-black text-yellow-500">1st</div>
                    <div className="text-xs text-zinc-500">American Ever</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-black text-yellow-500">∞</div>
                    <div className="text-xs text-zinc-500">Cultural Legacy</div>
                  </div>
                </div>

              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Membership Required Modal */}
      <AnimatePresence>
        {showMembershipPrompt && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
            onClick={() => setShowMembershipPrompt(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-zinc-900 border border-amber-500/30 rounded-2xl p-8 max-w-md w-full text-center"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-8 h-8 text-amber-400" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Members Only Content</h3>
              <p className="text-zinc-400 mb-6">
                The budget proposals and project summary documents are exclusive to Escola de Samba Casa Samba members. 
                Sign in or create an account to access these materials.
              </p>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowMembershipPrompt(false);
                    navigate('/admin');
                  }}
                  className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-black font-bold rounded-xl transition-all"
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setShowMembershipPrompt(false);
                    navigate('/admin');
                  }}
                  className="w-full py-3 border border-amber-500/50 hover:bg-amber-500/10 text-amber-400 font-bold rounded-xl transition-all"
                >
                  Create Free Account
                </button>
                <button
                  onClick={() => setShowMembershipPrompt(false)}
                  className="text-zinc-500 hover:text-zinc-300 text-sm mt-2"
                >
                  Maybe Later
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* Request Access Modal */}
        {showRequestAccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setShowRequestAccessModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={e => e.stopPropagation()}
              className="bg-[#141414] border border-amber-500/30 rounded-2xl p-8 max-w-md w-full"
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-amber-500" />
                </div>
                
                {downloadAccess.status === 'pending' ? (
                  <>
                    <h3 className="text-xl font-bold text-white mb-2">Request Pending</h3>
                    <p className="text-gray-400 mb-6">
                      Your request to access funding documents is being reviewed. 
                      You will be notified once approved.
                    </p>
                    <button
                      onClick={() => setShowRequestAccessModal(false)}
                      className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-lg"
                    >
                      Got It
                    </button>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-bold text-white mb-2">Request Download Access</h3>
                    <p className="text-gray-400 mb-6 text-sm">
                      These funding documents are protected. Please tell us why you'd like access.
                    </p>
                    
                    <div className="space-y-4 text-left">
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Name *</label>
                        <input
                          type="text"
                          value={requestForm.name}
                          onChange={e => setRequestForm(f => ({ ...f, name: e.target.value }))}
                          className="w-full bg-black/50 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                          placeholder="Your name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Email *</label>
                        <input
                          type="email"
                          value={requestForm.email}
                          onChange={e => setRequestForm(f => ({ ...f, email: e.target.value }))}
                          className="w-full bg-black/50 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                          placeholder="your@email.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Organization</label>
                        <input
                          type="text"
                          value={requestForm.organization}
                          onChange={e => setRequestForm(f => ({ ...f, organization: e.target.value }))}
                          className="w-full bg-black/50 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none"
                          placeholder="Company or institution (optional)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm text-gray-400 mb-1">Why do you need access? *</label>
                        <textarea
                          value={requestForm.reason}
                          onChange={e => setRequestForm(f => ({ ...f, reason: e.target.value }))}
                          rows={3}
                          className="w-full bg-black/50 border border-gray-700 rounded-lg px-4 py-2 text-white focus:border-amber-500 focus:outline-none resize-none"
                          placeholder="Please explain your interest in this project..."
                        />
                      </div>
                    </div>
                    
                    <div className="flex gap-3 mt-6">
                      <button
                        onClick={() => setShowRequestAccessModal(false)}
                        className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={submitAccessRequest}
                        disabled={submittingRequest}
                        className="flex-1 px-4 py-3 bg-amber-500 hover:bg-amber-600 text-black font-bold rounded-lg disabled:opacity-50"
                      >
                        {submittingRequest ? 'Submitting...' : 'Submit Request'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Legacy = () => (
  <>
    <LegacyInner />
    <AdminEditorLink to="/admin/dashboard#about-editor-btn" label="Edit Legacy content" />
  </>
);

export default Legacy;
